import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, GitBranch, Eye, GitMerge, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Scenarios() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/scenarios");
      setScenarios(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) {
      toast.error("Name required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/scenarios", draft);
      toast.success("Scenario created");
      setCreateOpen(false);
      setDraft({ name: "", description: "" });
      await load();
      navigate(`/organogram?scenario=${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
    setCreating(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;
    try {
      await api.delete(`/scenarios/${id}`);
      toast.success("Scenario deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const merge = async (id) => {
    if (!window.confirm("Apply all changes from this scenario to the live org chart? This cannot be undone (but history is recorded).")) return;
    try {
      const { data } = await api.post(`/scenarios/${id}/merge`);
      toast.success(`Merged — ${data.applied} employees updated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            What-if · Reorg drafts
          </p>
          <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Scenarios
          </h1>
          <p className="mt-2 max-w-xl text-sm text-neutral-600">
            Sketch reorgs without affecting the live org chart. Edits applied inside a scenario stay isolated until you merge them.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="new-scenario-button">
          <Plus size={14} className="mr-2"/>
          New scenario
        </Button>
      </div>

      <div className="border border-neutral-200 bg-white">
        {scenarios.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <GitBranch size={28} className="mx-auto mb-3 text-neutral-300"/>
            <p className="text-sm text-neutral-600">No scenarios yet. Create one to start sketching a reorg.</p>
          </div>
        ) : (
          <ul>
            {scenarios.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-4 border-b border-neutral-100 px-6 py-4 last:border-0" data-testid={`scenario-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{s.name}</p>
                    {s.status === "merged" ? (
                      <Badge variant="outline" className="rounded-none border-[#0A7029] text-[10px] uppercase text-[#0A7029]">
                        <CheckCircle2 size={9} className="mr-1"/>Merged
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-none border-neutral-300 text-[10px] uppercase">Draft</Badge>
                    )}
                  </div>
                  {s.description && <p className="truncate text-xs text-neutral-600">{s.description}</p>}
                  <p className="mt-1 flex gap-3 text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    <span>{s.overrides_count} changes</span>
                    <span>·</span>
                    <span>by {s.created_by}</span>
                    <span>·</span>
                    <span>{new Date(s.created_at).toLocaleDateString()}</span>
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/organogram?scenario=${s.id}`)}
                    className="rounded-none border-neutral-300"
                    data-testid={`open-scenario-${s.id}`}
                  >
                    <Eye size={13} className="mr-1.5"/>
                    Open
                  </Button>
                  {s.status === "draft" && (
                    <Button
                      size="sm"
                      onClick={() => merge(s.id)}
                      disabled={!isAdmin || s.overrides_count === 0}
                      className="rounded-none bg-[#0A7029] text-white hover:bg-[#085A22]"
                      data-testid={`merge-scenario-${s.id}`}
                      title={isAdmin ? "Apply to live org" : "Admin only"}
                    >
                      <GitMerge size={13} className="mr-1.5"/>
                      Merge
                    </Button>
                  )}
                  <button onClick={() => remove(s.id)} disabled={!isAdmin} className="p-2 text-neutral-400 hover:text-[#C50F1F] disabled:opacity-30" title="Delete">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>New scenario</DialogTitle>
            <DialogDescription className="text-xs text-neutral-600">
              Give the scenario a memorable name. You can then edit employees inside the Organogram and your changes will stay scoped to this scenario until you merge or discard it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Q2 2026 reorg proposal" className="rounded-none" data-testid="scenario-name-input"/>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Description (optional)</Label>
              <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="rounded-none" rows={3} data-testid="scenario-description-input"/>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={creating} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="scenario-create-submit">
              {creating ? "Creating…" : "Create + open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
